/**
 * Утилиты для маппинга статусов заказов в CSS классы и текстовые сообщения
 * 
 * Следует принципу Open/Closed: легко расширяется без изменения существующего кода
 */

import type { OrderSummary, Order } from '../model/types'
import { 
  ORDER_STATUS_CONFIG, 
  PAYMENT_STATUS_CONFIG, 
  DEFAULT_STATUS_CONFIG,
  type StatusConfig, 
} from './orderStatusConfig'

/**
 * Интерфейс для результата маппинга статуса
 */
export interface MappedStatus {
  badgeClass: string
  message: string
  icon?: string
  priority: number
}

/**
 * Получает наиболее приоритетный статус для заказа
 * 
 * Логика приоритетов:
 * 1. Критичные статусы оплаты (FAILED, CANCELLED, REFUNDED) - приоритет 1-9
 * 2. Статус заказа (если есть) - приоритет 10-19
 * 3. Обычные статусы оплаты - приоритет 20-29
 * 4. По умолчанию - приоритет 99
 * 
 * @param orderSummary - краткая или полная информация о заказе
 * @returns конфигурация статуса с наивысшим приоритетом
 */
export function getHighestPriorityStatus(
  orderSummary: OrderSummary | Order | null,
): StatusConfig {
  if (!orderSummary) {
    return DEFAULT_STATUS_CONFIG
  }

  const candidates: StatusConfig[] = []

  // Добавляем статус заказа (если есть)
  if (orderSummary.status) {
    const config = ORDER_STATUS_CONFIG[orderSummary.status]
    if (config) {
      candidates.push(config)
    }
  }

  // Добавляем статус оплаты (если есть)
  if (orderSummary.paymentStatus) {
    const config = PAYMENT_STATUS_CONFIG[orderSummary.paymentStatus]
    if (config) {
      candidates.push(config)
    }
  }

  // Если нет кандидатов, возвращаем по умолчанию
  if (candidates.length === 0) {
    return DEFAULT_STATUS_CONFIG
  }

  // Сортируем по приоритету (меньше = выше) и возвращаем первый
  return candidates.sort((a, b) => a.priority - b.priority)[0]
}

/**
 * Получает CSS класс бейджа для заказа
 * 
 * @param orderSummary - информация о заказе
 * @returns CSS класс для бейджа статуса
 */
export function getStatusBadgeClass(orderSummary: OrderSummary | Order | null): string {
  const status = getHighestPriorityStatus(orderSummary)
  return status.badgeClass
}

/**
 * Получает текстовое сообщение статуса для заказа
 * 
 * @param orderSummary - информация о заказе
 * @returns текстовое описание статуса
 */
export function getStatusMessage(orderSummary: OrderSummary | Order | null): string {
  const status = getHighestPriorityStatus(orderSummary)
  return status.message
}

/**
 * Получает иконку статуса для заказа
 * 
 * @param orderSummary - информация о заказе
 * @returns имя класса иконки или undefined
 */
export function getStatusIcon(orderSummary: OrderSummary | Order | null): string | undefined {
  const status = getHighestPriorityStatus(orderSummary)
  return status.icon
}

/**
 * Получает полную информацию о статусе заказа
 * 
 * @param orderSummary - информация о заказе
 * @returns полный маппинг статуса
 */
export function getMappedStatus(orderSummary: OrderSummary | Order | null): MappedStatus {
  const status = getHighestPriorityStatus(orderSummary)
  return {
    badgeClass: status.badgeClass,
    message: status.message,
    icon: status.icon,
    priority: status.priority,
  }
}

